import React, { useState, useEffect } from "react";
import { Link } from "gatsby";
import styled, { css } from "styled-components";
import { Button } from "./Button.js";
import Scrollspy from "react-scrollspy";
import AuthenticateLogo from "./AuthenticateLogo";

//Images
import Logo from "../images/logos/Logo_dark.svg";
import Logo_blue from "../images/logos/Logo_blue.svg";
import Menu_icon from "../images/menu_bar.svg";
import Hover_icon from "../images/Subtract.svg";

const Container = styled.nav`
  background: ${(props) =>
    props.navbar
      ? "white"
      : '#F3E8FC'};
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 10;

  ${({ whiteBg }) =>
    whiteBg &&
    css`
      background: white;
    `}

  @media screen and (max-width: 912px) {
    margin-top: 0;
  }
`;

const Nav = styled.div`
  max-width: 1280px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 8px 24px;
  margin: 0 auto;

  @media screen and (max-width: 912px) {
    padding: 12px 20px;
  }
`;

const Image = styled.img`
  width: 144px;
  height: 18px;
`;

const NavButton = styled(Button)`
  transition: all 0.2s;

  ${({ hide }) =>
    hide &&
    css`
      margin-left: 16px;
      display: ${(props) => (props.navbar ? "inline-block" : "none")};
    `}

  @media screen and (max-width: 912px) {
    display: none;
  }
  a {
    text-decoration: none;
    color: ${({ secondary }) => (secondary ? "#212121" : "white")};
  }

  &:hover {
    transform: scale(1.1);
  }
`;

const NavLinks = styled.div`
  padding: 0;
  margin-left: 30px;
  @media screen and (max-width: 912px) {
    position: absolute;
    top: 50px;
    left: 0;
    background: #fafafa;
    display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
    flex-direction: column;
    width: 100%;
    text-align: center;
    padding: 0 0 20px;
    margin-left: 0;
  }
`;

const ScrollSpyDiv = styled(Scrollspy)`
  @media screen and (max-width: 912px) {
    display: flex;
    flex-direction: column;
  }
`;

const PageLink = styled(Link)`
  margin: 12px;
  font-size: 14px;
  font-weight: 400;
  text-decoration: none;
  color: #212121;
  padding-bottom: 5px;

  &:hover {
    background: url(${Hover_icon}) no-repeat;
    background-position: center bottom;
  }

  &.active {
    background: url(${Hover_icon}) no-repeat;
    background-position: center bottom;
  }
`;

const SignInLink = styled(Button)`
  display: none;
  transition: all 0.2s;

  a {
    text-decoration: none;
    color: white;
  }

  &:hover {
    transform: scale(1.1);
  }

  @media screen and (max-width: 912px) {
    display: block;
    margin-bottom: 24px;
    margin: 0 auto;
  }
`;

const MenuBar = styled.div`
  display: none;

  @media screen and (max-width: 912px) {
    display: block;
    cursor: pointer;
    padding: 4px;
    /* background: #f9fafb; */
    background: ${({ isOpen }) => (isOpen ? "#f9fafb" : "none")};
    border-radius: 4px;
    width: 32px;
    height: 32px;
  }
`;

const MenuIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const NavBtnGroup = styled.div`
  @media screen and (max-width: 912px) {
    display: none;
  }
`;

const LoginLink = styled(Link)`
  margin: 12px;
  font-weight: 400;
  font-size: 14px;
  line-height: 19px;
  text-decoration: none;
  color: #212121;
  padding-bottom: 5px;
  ${({ disable }) =>
    disable
      ? css`
          display: none;
        `
      : css`
          display: initial;
        `}

  @media screen and (max-width: 912px) {
    display: block;
  }
`;

const ImageLink = styled(Link)``;

const NavWrapper = styled.div`
  display: flex;

  @media screen and (max-width: 912px) {
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
`;

function Navbar({ whiteBg }) {
  const [isOpen, setIsOpen] = useState(false);
  const [navbarLogo, setNavbarLogo] = useState(Logo);
  const [navbar, setNavbar] = useState(false);

  const toggleNav = () => {
    setIsOpen((open) => !open);
  };

  const changeLogo = () => {
    if (window.scrollY >= 60) {
      setNavbarLogo(Logo_blue);
    } else {
      setNavbarLogo(Logo);
    }
  };

  useEffect(() => {
    changeLogo();
    window.addEventListener("scroll", changeLogo);
  });

  const changeBackground = () => {
    if (window.scrollY >= 66) {
      setNavbar(true);
    } else {
      setNavbar(false);
    }
  };

  useEffect(() => {
    changeBackground();
    window.addEventListener("scroll", changeBackground);
  });

  return (
    <Container navbar={navbar} whiteBg={whiteBg}>
      <Nav>
        <NavWrapper>
          <ImageLink to="/">
            <Image
              src={navbarLogo}
              alt="Authenticating.com"
              width="auto"
              height="auto"
              loading="lazy"
            />
          </ImageLink>
          <MenuBar onClick={toggleNav} isOpen={isOpen}>
            <MenuIcon
              src={Menu_icon}
              alt="Menu Icon"
              width="auto"
              height="auto"
              loading="lazy"
            />
          </MenuBar>

          <NavLinks className="nav_links" isOpen={isOpen}>
            <ScrollSpyDiv
              items={[
                "solution",
                "pricing",
                "contact",
                "documentation",
                "resources",
              ]}
              currentClassName="active"
            >
              <PageLink to="/#solution">Solutions</PageLink>
              <PageLink to="/#pricing">Pricing</PageLink>
              <PageLink to="/#contact">Contact</PageLink>
              <PageLink
                to="https://docs.authenticating.com/"
                target="_blank"
                rel="noreferrer"
              >
                Documentation
              </PageLink>
              <PageLink to="/resources/blog">Resources</PageLink>
            </ScrollSpyDiv>
            <LoginLink
              to="https://portal.authenticating.com"
              target="_blank"
              rel="noreferrer"
              disable
            >
              Log in
            </LoginLink>
            <SignInLink>
              <a
                href="https://portal.authenticating.com/register"
                target="_blank"
                rel="noreferrer"
              >
                Get started
              </a>
            </SignInLink>
          </NavLinks>
        </NavWrapper>

        <NavBtnGroup>
          <LoginLink
            to="https://portal.authenticating.com"
            target="_blank"
            rel="noreferrer"
          >
            Log in
          </LoginLink>
          <NavButton secondary>
            <a
              href="https://portal.authenticating.com/register"
              target="_blank"
              rel="noreferrer"
            >
              Get started
            </a>
          </NavButton>
          <NavButton hide navbar={navbar}>
            <a
              href="https://verify.authenticating.com/onboarding"
              target="_blank"
              rel="noreferrer"
            >
              <AuthenticateLogo />
            </a>
          </NavButton>
        </NavBtnGroup>
      </Nav>
    </Container>
  );
}

export default Navbar;
